import { useEffect, useRef, useState } from "react";

/**
 * Inline editable text cell — click / Enter to edit, Enter/blur to save, Esc to cancel.
 * Mirrors InlineEditNumber but for free-text fields (e.g. category).
 */
export default function InlineEditText({
    value,
    onSave,
    placeholder = "-",
    testId,
    className = "",
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? "");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { if (!editing) setDraft(value ?? ""); }, [value, editing]);
    useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

    const commit = async () => {
        if (saving) return;
        const next = (draft ?? "").trim();
        if (next === (value ?? "").trim()) { setEditing(false); return; }
        setSaving(true);
        try {
            await onSave(next);
            setEditing(false);
        } catch {
            setDraft(value ?? "");
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => { setDraft(value ?? ""); setEditing(false); };

    if (!editing) {
        return (
            <button
                type="button"
                data-testid={testId}
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                className={`inline-block max-w-full text-left px-2 py-0.5 rounded truncate hover:bg-gold-100 focus:bg-gold-100 focus:outline-none focus:ring-1 focus:ring-gold-400 cursor-text ${className}`}
                title="点击编辑"
            >
                {value ? value : <span className="text-slate-300">{placeholder}</span>}
            </button>
        );
    }

    return (
        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <input
                ref={inputRef}
                data-testid={`${testId}-input`}
                type="text"
                value={draft}
                disabled={saving}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commit(); }
                    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
                }}
                onBlur={commit}
                className="h-7 w-32 px-2 rounded border border-gold-400 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gold-500 disabled:opacity-60"
                placeholder={placeholder}
            />
            {saving && <span className="text-[10px] text-gold-500">…</span>}
        </div>
    );
}
